/** Agent status WS protocol & labels (SSOT). Status frames are EPHEMERAL: broadcast via ChatRoom DO, never persisted to
 *  D1 -- only the final assistant message is. Stages map 1:1 to OpenAI Agents SDK stream events (received -> before runner.run; thinking -> first raw_model_stream_event; complete -> after streamed.completed). */

/** Discriminator for the WS frame -- consumers branch on this. */
export const AGENT_STATUS_WS_TYPE = 'agent_status' as const;

export const AGENT_STATUS_STAGES = {
	RECEIVED: 'received',
	THINKING: 'thinking',
	TOOL_CALLED: 'tool_called',
	TOOL_OUTPUT: 'tool_output',
	REASONING: 'reasoning',
	HANDOFF: 'handoff',
	AGENT_CHANGED: 'agent_changed',
	MESSAGE_OUTPUT: 'message_output',
	COMPLETE: 'complete',
	ERROR: 'error',
	CANCELLED: 'cancelled',
} as const;

export type AgentStatusStage = (typeof AGENT_STATUS_STAGES)[keyof typeof AGENT_STATUS_STAGES];

/**
 * Stage -> default human label used when no tool-specific override applies.
 *
 * `error: 'Something went wrong'` is EQUAL BYTES, DIFFERENT DECISION from `TITLE_FEDERA_ERROR`
 * (`const/apps-federa-title-values.ts`) and `ROOT_ERROR_BOUNDARY_COPY.title` (`const/root-error-boundary.ts`) -- the
 * third member of a pair those two leaves already adjudicate, recorded here so an SSOT census reads the verdict
 * instead of re-deriving it. This one is a per-STAGE label in a `Record<AgentStatusStage, string>` whose other ten
 * members are chat-progress prose ("Thinking", "Almost ready"); it is picked by the stage discriminant and must stay
 * tonally beside its siblings, not beside a page title or a boundary heading. FALSIFIER: the day one surface owns
 * every error-facing string -- a shared error-copy leaf keyed by SURFACE rather than by stage -- all three fold onto
 * it and this note dies.
 */
export const AGENT_STATUS_STAGE_LABELS: Record<AgentStatusStage, string> = {
	received: 'Thinking',
	thinking: 'Thinking',
	tool_called: 'Working on it',
	tool_output: 'Reviewing the information',
	reasoning: 'Thinking it through',
	handoff: 'Getting you the right help',
	agent_changed: 'Getting you the right help',
	message_output: 'Almost ready',
	complete: 'Done',
	error: 'Something went wrong',
	cancelled: 'Cancelled',
};

/** Tool fn name -> user-facing label. Keep prose; no internal jargon. Compacted (multiple per line) to satisfy the const max-lines cap; one entry per line was 50+ effective lines. Order preserved key-asc within each row. */
export const AGENT_TOOL_LABELS = {
	activity_summary: 'Summarising activity',
	add_licence: 'Adding license',
	buy_transaction: 'Setting up your transaction',
	// The generic label for an endpoint call whose action has no label of its own (another company's endpoint): a
	// federa-ai-tools call is labelled by the action it runs instead (`classifyRunItemBroadcast`).
	call_endpoint: 'Getting that for you',
	check_verification_code: 'Checking your code',
	contact_add: 'Saving the contact',
	create_issue: 'Creating the ticket',
	fetch_account_details: 'Pulling your account details',
	fetch_property: 'Looking up the property',
	fetch_user_ledger_transactions: 'Reviewing your transactions',
	fetch_user_licenses: 'Loading your licences',
	fetch_user_refundable_transactions: 'Finding refundable top-ups',
	initiate_refund: 'Initiating refund',
	new_user: 'Setting up user invitation',
	search_properties: 'Finding homes for you',
	send_verification_code: 'Sending you a code',
	update_licence: 'Updating licence',
	vector_search: 'Checking what I know',
} as const satisfies Record<string, string>;

/** {@link AGENT_TOOL_LABELS} under a WIDE key type, for the runtime lookups: an SDK tool name and a rendered card title
 *  both arrive as a plain `string`, which cannot index the literal key set the `as const` above produces. Declared HERE,
 *  once, 2026-09-05: the identical widening line -- annotation, initializer and even the justifying comment -- had been
 *  copied privately into `utils/assistant/agent-run-status-labels.ts` and `components/domain-cards/FunctionCard.tsx`,
 *  which is one decision spelled in three places and two chances for the views to drift off the labels they view. */
export const AGENT_TOOL_LABEL_BY_NAME: Readonly<Record<string, string>> = AGENT_TOOL_LABELS;

/** Internal/admin tool names that should NOT surface to end users -- show a generic label instead. */
export const AGENT_TOOL_INTERNAL: ReadonlySet<string> = new Set<string>([]);
